import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeviceDetailsDialogData } from './device-details-dialog-data';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { sortBy as _sortBy } from 'lodash';
import { Observable, map, startWith, zip } from 'rxjs';
import { EquipmentSearchService } from 'src/app/shared/equipment-search/equipment-search.service';
import { DeviceTypeDto } from 'src/app/core/services/device-inventory/device-type.dto';
import { DeviceDto } from 'src/app/core/services/device-inventory/device.dto';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { DeviceInventoryService } from 'src/app/core/services/device-inventory.service';
import { ADPResourceService, CustomFormControl } from 'digital-core';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';

@Component({
  selector: 'amfe-device-details-dialog',
  templateUrl: './device-details-dialog.component.html',
  styleUrls: ['./device-details-dialog.component.scss'],
})
export class DeviceDetailsDialogComponent implements OnInit, AfterViewInit {
  form: UntypedFormGroup;
  loaded: boolean = false;
  allDeviceTypes: DeviceTypeDto[] | undefined;
  gadgetDeviceTypes: DeviceTypeDto[] | undefined;
  clientSoldDeviceTypes: DeviceTypeDto[] | undefined;
  selectableDeviceTypes: any[] = [];
  loadingDeviceDetailsMessage = 'Loading Device Details';
  hasError: boolean = false;
  isMakesAutoCompleteEnabled: boolean = true;
  errorHeading = '';
  errorMessage = '';
  isValidDevice = false;
  deviceCheck: any = {};
  makes: any;
  filteredMakes: Observable<string[]>;
  dateRegex = /(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.](19|20)\d\d/;
  deviceDto: PghUserDeviceBase = {};

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: DeviceDetailsDialogData,
    private deviceInventoryService: DeviceInventoryService,
    private equipmentSearchService: EquipmentSearchService,
    private resourceService: ADPResourceService,
    private selectedDeviceService: SelectedDeviceService
  ) {
    this.form = this.createForm(null);
  }

  ngAfterViewInit(): void {

  }

  ngOnInit(): void {
    if (!this.data.selectedDevice) {
      this.getDeviceTypes(this.data.inventoryId, this.data.clientSoldInventoryId);
      this.getMakes();
    } else {
      this.loaded = true;
      this.setNickname();
    }
    this.loadingDeviceDetailsMessage = this.resourceService.getPortaLabelValue("DeviceDetails_Modal_Loading_Device_Details");
    this.isMakesAutoCompleteEnabled = this.resourceService.getPortalSetting("DeviceDetails_Modal_Makes_Autocomplete_Enabled").Value==='true';
  }

  getResource(name: string, namespace = null) {
    namespace = namespace ? namespace : "DeviceDetails_Modal";
    let title = namespace + "_" + name;
    let resourceValue = this.resourceService.getViewResourceObject(title);
    return resourceValue;
  }

  onDeviceChange() {
    this.getDeviceFromForm();

    if (this.data.formFields.includes('deviceType')) {
      this.deviceCheck.DeviceTypeIsValid = (this.deviceDto.deviceTypeCode?.length ?? 0) > 0;
    }
    if (this.data.formFields.includes('make')) {
      this.deviceCheck.MakeIsValid = this.deviceDto.make.length > 0;
    }
    if (this.data.formFields.includes('model')) {
      this.deviceCheck.ModelIsValid = this.deviceDto.model.length > 0;
      if (!this.deviceCheck.ModelIsValid) {
        this.form.controls['model'].setErrors({invalidModelError: this.getResource('Model').ValidationMessage});
      } else {
        this.form.controls['model'].setErrors(null);
      }
    }
    if (this.deviceCheck.MakeIsValid && this.deviceCheck.ModelIsValid) {
      this.setNickname();
    }
    if (this.data.formFields.includes('nickName')) {
      this.deviceCheck.NickNameIsValid = !this.form.controls['nickName']?.hasError('pattern');
      if(!this.deviceCheck.NickNameIsValid ){
        this.form.controls['nickName']?.markAsTouched();
      }
    }
    this.updateDeviceValidity();
  }

  onBuyTypeChange() {
    this.deviceDto.buyType = this.form.controls['buyType']?.value || '';
    this.deviceCheck.BuyTypeIsValid = this.deviceDto.buyType.length > 0;
    if (this.deviceCheck.BuyTypeIsValid) {
      this.deviceDto.inventoryId = this.deviceDto.buyType?.toLowerCase() == 'yes' ? this.data.clientSoldInventoryId : this.data.inventoryId;
      let deviceTypeCounter = 0;
      let deviceTypes = this.deviceDto.buyType?.toLowerCase() == 'yes' ? this.clientSoldDeviceTypes : this.gadgetDeviceTypes;
      this.selectableDeviceTypes = [];
      deviceTypes.forEach(element => {
        this.selectableDeviceTypes.push({
          Id: element.id,
          Key: element.id.toString(),
          Value: element.name,
          counter: deviceTypeCounter.toString()
        });
        deviceTypeCounter++;
      });
      if (this.data.formFields.includes('deviceType') && this.deviceCheck.DeviceTypeIsValid) {
        let selectedDeviceTypeId: string = this.form.controls['deviceType'].value;
        let selectedDeviceType: DeviceTypeDto = deviceTypes.find((dt) => dt.id?.toString() === selectedDeviceTypeId);
        if (!selectedDeviceType?.code && !selectedDeviceType?.name) {
          this.deviceCheck.BuyTypeIsValid = false;
          this.form.controls['deviceType'].setValue('');
        }
      }
    }
    this.updateDeviceValidity();
  }

  onPurchasePriceChange() {
    const ppResource = this.getResource('PurchasePrice');
    let purchasePrice = this.form.controls['purchasePrice']?.value;
    if (purchasePrice && (purchasePrice.includes('$') || purchasePrice.includes(','))) {
      purchasePrice = purchasePrice.replace('$','').replace('.00','').replaceAll(',','');
    }
    if (purchasePrice) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      });
      this.form.controls['purchasePrice'].setValue(formatter.format(purchasePrice));
    }
    this.deviceCheck.PurchasePriceIsValid = purchasePrice > ppResource.MinValue && purchasePrice <= ppResource.MaxValue;
    if (this.deviceCheck.PurchasePriceIsValid) {
      this.deviceDto.purchasePrice = purchasePrice;
      this.form.controls['purchasePrice'].setErrors(null);
    } else {
      if (purchasePrice) {
        this.form.controls['purchasePrice'].setErrors({invalidPriceError: this.resourceService.getPortaLabelValue('DeviceDetails_Modal_PurchasePrice_Range_Error')});
      } else {
        this.form.controls['purchasePrice'].setErrors({invalidPriceError: ppResource.ValidationMessage});
      }
      this.deviceDto.purchasePrice = undefined;
    }
    this.updateDeviceValidity();
  }
  onPPClick() {
    const pp = this.form.controls['purchasePrice']?.value;
    if (pp && (pp.includes('$') || pp.includes(','))) {
      this.form.controls['purchasePrice'].setValue(pp.replace('$','').replace('.00','').replaceAll(',',''));
    } else if (pp && pp.includes('NaN')) {
      this.form.controls['purchasePrice'].setValue('');
    }
  }

  // decimal check on key press
  onPPKeyPress(e) {
    const input = e.target as HTMLInputElement;
    let value = input.value;

    // Allow only digits and one decimal point
    const allowedChars = /^[0-9.]+$/;
    if (!allowedChars.test(e.key)) {
      e.preventDefault();
      return;
    }

    // Allow only one decimal point
    if (e.key === '.' && value.includes('.')) {
      e.preventDefault();
      return;
    }

    // Limit to two decimal places
    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1] && parts[1].length > 2) {
        e.preventDefault();
      }
    }
  }

  onDateChange() {
    const purchaseDate = this.form.controls['purchaseDate']?.value || '';
    const minValue =  Number(this.resourceService.getPortalSetting("DeviceDetails_Modal_PurchaseDate_MinDate_Days").Value);
    //const minAllowedDate = new Date(this.data.contractStartDate);
    const minAllowedDate = new Date();
    minAllowedDate.setDate(minAllowedDate.getDate() - minValue);

    if (this.dateRegex.test(purchaseDate) || (purchaseDate instanceof Date)) {
      if (new Date().setHours(0,0,0,0) <= new Date(purchaseDate).setHours(0,0,0,0)) {
        this.form.controls['purchaseDate'].setErrors({futureDateError: this.resourceService.getPortaLabelValue('DeviceDetails_Modal_PurchaseDate_Range_Error')});
        this.deviceCheck.PurchaseDateIsValid = false;
        this.deviceDto.purchaseDate = undefined;
      } else if(new Date(purchaseDate) < minAllowedDate){
        this.form.controls['purchaseDate'].setErrors({minDateError: this.resourceService.getPortaLabelValue('DeviceDetails_Modal_PurchaseDate_MinDate_Error')});
        this.deviceCheck.PurchaseDateIsValid = false;
        this.deviceDto.purchaseDate = undefined;
      }
      else {
        this.form.controls['purchaseDate'].setErrors(null);
        this.deviceCheck.PurchaseDateIsValid = true;
        this.deviceDto.purchaseDate = purchaseDate;
      }
    } else {
      this.form.controls['purchaseDate'].setErrors({invalidDateError: this.resourceService.getPortaLabelValue('Global_Error_Invalid_Date')});
      this.deviceCheck.PurchaseDateIsValid = false;
      this.deviceDto.purchaseDate = undefined;
    }
    this.updateDeviceValidity();
  }

  private getDeviceFromForm() {
    let selectedDeviceTypeId: string = undefined;
    let selectedDeviceType: DeviceTypeDto = undefined;

    if (this.data.formFields.includes('deviceType')) {
      selectedDeviceTypeId = this.form.controls['deviceType'].value;
      selectedDeviceType = this.allDeviceTypes?.find((dt) => dt?.id?.toString() === selectedDeviceTypeId);
      this.deviceDto.deviceTypeCode = selectedDeviceType?.code;
      this.deviceDto.deviceTypeName = selectedDeviceType?.name;
    }
    if (this.data.formFields.includes('make')) {
      this.deviceDto.make = this.form.controls['make']?.value || '';
    }
    if (this.data.formFields.includes('model')) {
      this.deviceDto.model = this.form.controls['model']?.value || '';
    }
    if (this.data.formFields.includes('serialNumber')) {
      this.deviceDto.serialNumber = this.form.controls['serialNumber']?.value || '';
    }
    if (this.data.formFields.includes('nickName')) {
      this.deviceDto.nickName = this.form.controls['nickName']?.value || '';
    }
    /*Removed the inventoryId assignment as in the handleAddDevice method in home-device-details.component
      has the login to add the right inventory.*/
    // this.deviceDto.inventoryId = selectedDeviceType?.code === 'SMPH' && this.data.phoneInventoryInfo?.id !== this.data.inventoryInfo?.id ? this.data.phoneInventoryInfo?.id : this.data.inventoryInfo?.id;
  }

  private updateDeviceValidity() {
    let isValid = true;
    let objList = Object.keys(this.deviceCheck);
    for (let index = 0; index < objList.length; index++) {
      const prop = objList[index];
      if (!this.deviceCheck[prop]) {
        isValid = false;
        break;
      }
    }
    this.isValidDevice = isValid;
  }

  private setNickname() {
    if (this.data.selectedDevice) {
      let count = 0;
      let nickname = `${this.data.selectedDevice?.make ?? ''} ${this.data.selectedDevice?.model ?? ''}`.trim();
      // If nickname exists, increment the counter and check again.
      while (this.selectedDeviceService.getAllDevices().find(x => x?.nickName?.toLowerCase() === nickname?.toLowerCase())) {
        count++;
        nickname = `${this.data.selectedDevice?.make ?? ''} ${this.data.selectedDevice?.model ?? ''} ${count}`.trim();
      }
      this.form.controls['nickName'].setValue(nickname);
    } else {
      const currentNickname = this.form.controls['nickName']?.value;
      const make = this.form.controls['make']?.value;
      const model = this.form.controls['model']?.value;
      if (!currentNickname) {
        let allDevices = [...this.data.devices, ...this.data.phoneDevices ?? []];
        let count = allDevices.filter(x => x.make?.toLowerCase() === make.toLowerCase() && x.model?.toLowerCase() === model.toLowerCase()).length;
        this.form.controls['nickName'].setValue(`${make} ${model} ${count > 0 ? count + 1 : ''}`.trim());
      }
    }
    this.deviceDto.nickName = this.form.controls['nickName']?.value;
  }

  private createForm(device: DeviceDto) {
    let deviceForm = new UntypedFormGroup({});
    if (this.data.formFields.includes('buyType')) {
      deviceForm.addControl('buyType', new CustomFormControl(''));
      this.deviceCheck.BuyTypeIsValid = false;
    }
    if (this.data.formFields.includes('deviceType')) {
      deviceForm.addControl('deviceType', new CustomFormControl(device?.deviceTypeId));
      this.deviceCheck.DeviceTypeIsValid = false;
    }
    if (this.data.formFields.includes('make')) {
      deviceForm.addControl('make', new CustomFormControl(device?.make));
      this.deviceCheck.MakeIsValid = false;
    }
    if (this.data.formFields.includes('model')) {
      deviceForm.addControl('model', new CustomFormControl(device?.model));
      this.deviceCheck.ModelIsValid = false;
    }
    if (this.data.formFields.includes('purchasePrice')) {
      deviceForm.addControl('purchasePrice', new CustomFormControl(null));
      this.deviceCheck.PurchasePriceIsValid = false;
    }
    if (this.data.formFields.includes('purchaseDate')) {
      deviceForm.addControl('purchaseDate', new CustomFormControl(null));
      this.deviceCheck.PurchaseDateIsValid = false;
    }
    if (this.data.formFields.includes('serialNumber')) {
      deviceForm.addControl('serialNumber', new CustomFormControl(device?.serialNumber));
      this.deviceCheck.SerialNumberIsValid = true;
    }
    if (this.data.formFields.includes('nickName')) {
      deviceForm.addControl('nickName', new CustomFormControl(device?.nickName, this.validateIsUnique.bind(this)));
      this.deviceCheck.NickNameIsValid = true;
    }
    return deviceForm;
  }

  private validateIsUnique(control: UntypedFormControl) {
    if (control?.value != null) {
      let allDevices = [...this.data.devices, ...this.data.phoneDevices ?? []];
      let count = allDevices.filter(x => x.nickName?.toLowerCase()?.trim() === control?.value?.toLowerCase()?.trim()).length;
      if (count > 0)
        return { pattern: true };
      else
        return null;
    } else
      return null;
  }

  private getMakes() {
    this.equipmentSearchService.manufacturersFromPtpIndex().subscribe(result => {
      this.makes = result['@search.facets'].Manufacturer.map(x => ({ Key: x.value, Value: x.value }));
      this.filteredMakes = this.form.controls['make'].valueChanges.pipe(
        startWith(''),
        map(value => this.filterMakes(value || ''))
      );
    });
  }

  private filterMakes(value: string): string[] {
    const filterValue = value.toLowerCase();
    let results = this.makes?.filter(option => option.toLowerCase().includes(filterValue));
    if (results?.find(term => term.toLowerCase() === value.toLowerCase())) {
      return results;
    }
    // Add search term to results so the user can select it if they want to.
    results = [value, ...results];
    return results;
  }

  private getDeviceTypes(inventoryId: number, clientSoldInventoryId: number) {
    // set the state to Loading so that while this method is going on, we will see a loading spinny circle
    let deviceTypeCounter = 0;
    const flag = this.resourceService.isPortalSettingTrue('Global_Client_IsClientSoldType');
    const task$ = this.deviceInventoryService.getDeviceTypes(inventoryId, flag);
    const cstask$ = this.deviceInventoryService.getDeviceTypes(clientSoldInventoryId, flag);
    zip(task$, cstask$).subscribe({
      next: (results: any[]) => {
        const resultCombined: any[] = [...new Set(...results)];
        this.gadgetDeviceTypes = _sortBy(results[0], (dv) => dv?.name?.toUpperCase());
        this.clientSoldDeviceTypes = _sortBy(results[1], (dv) => dv?.name?.toUpperCase());
        this.allDeviceTypes = _sortBy(resultCombined, (dv) => dv?.name?.toUpperCase());
        this.allDeviceTypes.forEach(element => {
          this.selectableDeviceTypes.push({
            Id: element.id,
            Key: element.id.toString(),
            Value: element.name,
            counter: deviceTypeCounter.toString()
          });
          deviceTypeCounter++;
        });
        this.loaded = true;
      },
      error: (error: HttpErrorResponse) => {
        this.hasError = true;
        this.errorHeading = this.resourceService.getPortaLabelValue("DeviceDetails_Modal_Error_Loading_Device_types");
        this.errorMessage = error.message;
      }
    });
  }
}
